"use client";

import { memo, useEffect, useMemo, type ReactNode } from "react";
import { VStack } from "@chakra-ui/react";
import { InstantSearchNext } from "react-instantsearch-nextjs";
import { MindstringPrototype } from "@/generated/fastapi";
import { useMeiliRuntime } from "@/core/search";
import { MEILI_SEARCH_CONFIG, SearchQueryConnector } from "@/features/search";
import {
  useMeiliRefreshState,
  useSelectionState,
  useSearchQuerySelect,
  useUserChoices,
  useGlobalStoreHydrated,
} from "@/state/globalStore";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import { mindstringPrototypesSlang } from "@/shared/config/backend";
import { PROFILE_SHAPING_EMPTY_STATES } from "@/features/profile-shaping/config/actions";
import { useSituationsQuery } from "@/features/situations";
import useStudioLayoutMap from "../hooks/useStudioLayoutMap";
import useStudioClientReady from "../hooks/useStudioClientReady";
import StudioLayout from "../layout/StudioLayout";
import StudioEmptyState from "../components/StudioEmptyState";
import StudioFreeListArrangementPanel from "./StudioFreeListArrangementPanel";
import StudioFreeListSearchPanel from "./StudioFreeListSearchPanel";
import StudioSituationArrangementPanel from "./StudioSituationArrangementPanel";
import StudioSituationSearchPanel from "./StudioSituationSearchPanel";
import { filterStudioLayoutMapBySituationLoadState } from "../utils";

const FILTERED_EMPTY_MESSAGE =
  "You still have situations in other views. Switch view to continue working with them.";

function formatPrototypeScopeLabel(prototype: MindstringPrototype) {
  const label = mindstringPrototypesSlang[prototype].label;

  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
}

function StudioWorkspaceViewContent() {
  const { selectedSituations } = useSelectionState();
  const {
    activeStudioGrouping,
    activeStudioExportLock,
    activePrototypeCategory,
    studioSituationLoadState,
    normalizeStudioSituationLoadState,
    targetsToRerank,
  } = useUserChoices();
  const { meiliRefreshStamp } = useMeiliRefreshState();
  const searchQueryState = useSearchQuerySelect();
  const situationsQuery = useSituationsQuery();
  const hasStableClientState = useGlobalStoreHydrated();
  const hasClientReady = useStudioClientReady();
  const canUsePersistedStudioLoadState =
    hasStableClientState && hasClientReady;
  const situations = useMemo(
    () => situationsQuery.data ?? [],
    [situationsQuery.data],
  );
  const { buildStudioLayoutMap, buildStudioLayoutMapFromSelection } =
    useStudioLayoutMap();
  const { searchClient } = useMeiliRuntime({
    surface: "studio_select",
    prototype: activePrototypeCategory,
  });

  const isLocked = activeStudioExportLock;
  const isFreeList = activeStudioGrouping === "free_list";
  const searchConfig = MEILI_SEARCH_CONFIG.thoughts;
  const refreshKey = meiliRefreshStamp["studio_thoughts"][
    activePrototypeCategory
  ];
  const rerankTarget =
    targetsToRerank.length > 0 ? targetsToRerank[0] : undefined;
  const studioLayoutMap = useMemo(() => {
    if (isFreeList) {
      return buildStudioLayoutMap(situations);
    }

    if (isLocked) {
      return buildStudioLayoutMapFromSelection(situations, selectedSituations);
    }

    return buildStudioLayoutMap(situations);
  }, [
    isFreeList,
    isLocked,
    situations,
    selectedSituations,
    buildStudioLayoutMap,
    buildStudioLayoutMapFromSelection,
  ]);
  const loadedStudioLayoutMap = useMemo(
    () =>
      isFreeList || !canUsePersistedStudioLoadState
        ? studioLayoutMap
        : filterStudioLayoutMapBySituationLoadState({
            layoutMap: studioLayoutMap,
            loadState: studioSituationLoadState,
          }),
    [
      canUsePersistedStudioLoadState,
      isFreeList,
      studioLayoutMap,
      studioSituationLoadState,
    ],
  );

  useEffect(() => {
    if (!canUsePersistedStudioLoadState) {
      return;
    }

    normalizeStudioSituationLoadState(situations);
  }, [
    canUsePersistedStudioLoadState,
    normalizeStudioSituationLoadState,
    situations,
  ]);

  const viewHeader = isLocked ? null : (
    <SearchQueryConnector
      query={searchQueryState.searchQuery}
      debounceMs={350}
      debugLabel="studio-select"
    />
  );

  const renderSituationContent = ({
    situationId,
    prototype,
  }: {
    situationId: string;
    prototype: MindstringPrototype;
  }): ReactNode => {
    if (isLocked) {
      return (
        <StudioSituationArrangementPanel
          prototype={prototype}
          rerankTarget={rerankTarget}
          situationId={situationId}
        />
      );
    }

    return (
      <StudioSituationSearchPanel
        prototype={prototype}
        situationId={situationId}
      />
    );
  };

  const renderNoSelectionFallback = ({
    prototypeCategory,
  }: {
    prototypeCategory: MindstringPrototype;
  }): ReactNode => {
    if (isFreeList) {
      if (isLocked) {
        return (
          <StudioFreeListArrangementPanel
            prototype={prototypeCategory}
            rerankTarget={rerankTarget}
          />
        );
      }

      return <StudioFreeListSearchPanel prototype={prototypeCategory} />;
    }

    const availableSituationCount =
      studioLayoutMap[prototypeCategory]?.length ?? 0;
    const loadedSituationCount =
      loadedStudioLayoutMap[prototypeCategory]?.length ?? 0;

    if (
      canUsePersistedStudioLoadState &&
      availableSituationCount > 0 &&
      loadedSituationCount === 0
    ) {
      return (
        <GenericEmptyComponent
          title="No situations loaded"
          message="Load situations from the toolbar to continue shaping this perspective."
          variant="inline"
        />
      );
    }

    const hasSituationsInOtherPrototypeViews = Object.entries(
      loadedStudioLayoutMap,
    ).some(
      ([prototype, situationIds]) =>
        prototype !== prototypeCategory && situationIds.length > 0,
    );

    if (hasSituationsInOtherPrototypeViews) {
      return (
        <GenericEmptyComponent
          title={`No situations in ${formatPrototypeScopeLabel(prototypeCategory)}`}
          message={`${FILTERED_EMPTY_MESSAGE} Shape another situation or switch perspective.`}
          variant="inline"
        />
      );
    }

    if (isLocked) {
      return (
        <GenericEmptyComponent
          title={PROFILE_SHAPING_EMPTY_STATES.orderMode.title}
          message={PROFILE_SHAPING_EMPTY_STATES.orderMode.message}
          variant="inline"
        />
      );
    }

    return (
      <GenericEmptyComponent
        title={PROFILE_SHAPING_EMPTY_STATES.situation.title}
        message={PROFILE_SHAPING_EMPTY_STATES.situation.message}
        variant="inline"
      />
    );
  };

  if (!searchClient) {
    return null;
  }

  if (!situationsQuery.isLoading && situations.length === 0) {
    return <StudioEmptyState />;
  }

  return (
    <InstantSearchNext
      key={refreshKey}
      indexName={searchConfig.indexName}
      searchClient={searchClient}
      routing={false}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <VStack align="stretch">
        <StudioLayout
          studioLayoutMap={loadedStudioLayoutMap}
          activeStudioGrouping={activeStudioGrouping}
          activeStudioExportLock={activeStudioExportLock}
          viewHeaderComponent={viewHeader}
          situationContentComponent={renderSituationContent}
          noSelectionFallback={renderNoSelectionFallback}
        />
      </VStack>
    </InstantSearchNext>
  );
}

function StudioWorkspaceView() {
  return <StudioWorkspaceViewContent />;
}

export default memo(StudioWorkspaceView);
